import {
  ADMIN_MEMBER_PAGE_SIZES,
  AdminMemberListQuery,
  type AdminMemberListQuery as AdminMemberListQueryType,
  type AdminMemberRow,
  type AdminMemberSort,
  formatDate,
  formatPhone,
  HEARD_FROM_LABELS,
  PROVINCE_LABELS,
  PROVINCES,
  USER_STATUS_LABELS,
} from '@myhome/contracts';
import { Badge } from '@myhome/ui/components/badge';
import { Button } from '@myhome/ui/components/button';
import { Input } from '@myhome/ui/components/input';
import { NativeSelect, NativeSelectOption } from '@myhome/ui/components/native-select';
import { CaretDown, CaretLeft, CaretRight, CaretUp, MagnifyingGlass } from '@myhome/ui/icons';
import { EmptyState } from '@myhome/ui/patterns/empty-state';
import { ListSkeleton } from '@myhome/ui/patterns/list-skeleton';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, stripSearchParams, useNavigate } from '@tanstack/react-router';
import { createColumnHelper, tableFeatures, useTable } from '@tanstack/react-table';
import { useEffect, useId, useState } from 'react';
import { adminMembersQuery } from '../lib/queries';

/** What an empty URL means. Kept out of the address bar so links stay short. */
const DEFAULTS = AdminMemberListQuery.parse({});

export const Route = createFileRoute('/_shell/members/')({
  // The schema itself (Standard Schema), so links may leave out anything with a default.
  validateSearch: AdminMemberListQuery,
  search: { middlewares: [stripSearchParams(DEFAULTS)] },
  component: MembersPage,
});

const features = tableFeatures({});
const helper = createColumnHelper<typeof features, AdminMemberRow>();
const emptyRows: AdminMemberRow[] = [];

/** Columns a phone hides, so the table still fits at 390px. */
const WIDE_ONLY = 'hidden md:table-cell';

const columns = helper.columns([
  helper.display({
    id: 'member',
    header: () => <SortHeader sort="lastName" label="Member" />,
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <Link
          to="/members/$memberNumber"
          params={{ memberNumber: row.original.memberNumber }}
          className="truncate font-medium text-navy-900 underline-offset-4 hover:underline"
        >
          {row.original.firstName} {row.original.lastName}
        </Link>
        <span className="truncate text-eyebrow text-neutral-600">{row.original.email}</span>
      </div>
    ),
  }),
  helper.accessor('memberNumber', {
    header: () => <SortHeader sort="memberNumber" label="Member no." />,
    cell: ({ getValue }) => <span className="tabular-nums">{getValue()}</span>,
  }),
  helper.accessor('phone', {
    header: 'Mobile',
    cell: ({ getValue }) => <span className="whitespace-nowrap tabular-nums">{formatPhone(getValue())}</span>,
  }),
  helper.display({
    id: 'area',
    header: 'Main address',
    cell: ({ row }) => <AreaCell row={row.original} />,
  }),
  helper.accessor('memberSince', {
    header: () => <SortHeader sort="memberSince" label="Joined" />,
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatDate(new Date(getValue()), { year: 'numeric', month: 'short' })}
      </span>
    ),
  }),
  helper.display({
    id: 'joinedVia',
    header: 'Joined via',
    cell: ({ row }) =>
      row.original.referralCode === null ? (
        <span className="text-neutral-600">{HEARD_FROM_LABELS[row.original.heardFrom]}</span>
      ) : (
        <span className="whitespace-nowrap">
          Agent code <span className="font-medium tabular-nums">{row.original.referralCode}</span>
        </span>
      ),
  }),
  helper.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusCell row={row.original} />,
  }),
]);

/** Which columns hide on a phone. */
const COLUMN_CLASS: Record<string, string> = {
  memberNumber: WIDE_ONLY,
  phone: 'hidden lg:table-cell',
  area: WIDE_ONLY,
  joinedVia: 'hidden lg:table-cell',
};

/**
 * The member directory. Search, filters, sort and page all live in the URL,
 * so any view can be shared by link (apps/admin/CLAUDE.md). Subscription
 * state joins the table when billing lands.
 */
function MembersPage() {
  const search = Route.useSearch();
  // Same key as MembersResults, so this shares its cache entry.
  const result = useQuery({ ...adminMembersQuery(search), placeholderData: keepPreviousData });

  const filtered =
    search.q !== undefined ||
    search.province !== undefined ||
    search.referred !== undefined ||
    search.status !== undefined;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="font-heading text-headline font-semibold text-navy-900">Members</h1>
          <p className="text-neutral-600">
            {result.data === undefined
              ? 'Loading members'
              : `${result.data.total.toLocaleString('en-ZA')} ${filtered ? 'matching' : 'in total'}`}
          </p>
        </div>
      </header>

      <Filters search={search} />

      <MembersResults search={search} filtered={filtered} />
    </div>
  );
}

/** The column that sorts by each sort key, for aria-sort on its header. */
const SORT_COLUMN: Record<string, AdminMemberSort> = {
  member: 'lastName',
  memberNumber: 'memberNumber',
  memberSince: 'memberSince',
};

function ariaSort(columnId: string, current: AdminMemberListQueryType) {
  if (SORT_COLUMN[columnId] !== current.sort) return undefined;
  return current.dir === 'asc' ? 'ascending' : 'descending';
}

/** The table, or the loading, error and empty states in its place. */
function MembersResults({ search, filtered }: { search: AdminMemberListQueryType; filtered: boolean }) {
  const result = useQuery({ ...adminMembersQuery(search), placeholderData: keepPreviousData });
  const table = useTable({ features, columns, data: result.data?.items ?? emptyRows });

  if (result.isError) {
    return (
      <StatusBanner
        tone="danger"
        title="We could not load the members"
        description="Check your connection and try again."
        action={
          <Button variant="outline" size="sm" onClick={() => result.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  if (result.data === undefined) return <ListSkeleton count={8} shape="row" />;
  if (result.data.items.length === 0) {
    return (
      <EmptyState
        icon={MagnifyingGlass}
        title={filtered ? 'No members match' : 'No members yet'}
        description={
          filtered
            ? 'Try fewer words, or clear the filters.'
            : 'Members appear here as soon as they register.'
        }
        action={
          filtered ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/members" search={{}}>
                Clear search and filters
              </Link>
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <div
        className="overflow-x-auto rounded-(--radius-card) border border-border bg-surface"
        aria-busy={result.isFetching}
      >
        <table className="w-full border-collapse text-left">
          <thead className="border-b border-border bg-navy-50">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={ariaSort(header.column.id, search)}
                    className={`px-3 py-2 text-eyebrow font-semibold text-neutral-700 uppercase tracking-[0.04em] ${COLUMN_CLASS[header.column.id] ?? ''}`}
                  >
                    {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={result.isPlaceholderData ? 'opacity-60' : undefined}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.original.memberNumber}
                className="border-t border-border first:border-t-0 hover:bg-navy-50/60"
              >
                {row.getAllCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`max-w-64 px-3 py-2 align-top text-navy-900 ${COLUMN_CLASS[cell.column.id] ?? ''}`}
                  >
                    <table.FlexRender cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager total={result.data.total} search={search} />
    </>
  );
}

/** Search box and filters. Each change goes back to page 1. */
function Filters({ search: current }: { search: AdminMemberListQueryType }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const searchId = useId();
  const [text, setText] = useState(current.q ?? '');

  // Keep the box in step when the URL changes underneath it (back button, clear
  // link), without trimming what they are still typing.
  useEffect(
    () => setText((typed) => (typed.trim() === (current.q ?? '') ? typed : (current.q ?? ''))),
    [current.q],
  );

  // Search as they type, once they pause. Replace, so typing does not fill the history.
  useEffect(() => {
    const q = text.trim() === '' ? undefined : text.trim();
    if (q === current.q) return;
    const timer = setTimeout(() => {
      void navigate({ search: (prev) => ({ ...prev, q, page: 1 }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, current.q, navigate]);

  const setFilter = (patch: Partial<AdminMemberListQueryType>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-end">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <label htmlFor={searchId} className="font-medium text-navy-900">
          Search
        </label>
        <div className="relative">
          <MagnifyingGlass
            size={18}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500"
          />
          <Input
            id={searchId}
            type="search"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Name, email, member number or mobile"
            className="pl-9"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 md:flex">
        <FilterSelect
          label="Province"
          value={current.province}
          onChange={(value) =>
            setFilter({
              province: value === '' ? undefined : (value as AdminMemberListQueryType['province']),
            })
          }
          options={PROVINCES.map((value) => ({ value, label: PROVINCE_LABELS[value] }))}
          anyLabel="All provinces"
        />
        <FilterSelect
          label="Agent referral"
          value={current.referred}
          onChange={(value) => setFilter({ referred: value === '' ? undefined : (value as 'yes' | 'no') })}
          options={[
            { value: 'yes', label: 'Referred by an agent' },
            { value: 'no', label: 'Not referred' },
          ]}
          anyLabel="Referred or not"
        />
        <FilterSelect
          label="Status"
          value={current.status}
          onChange={(value) =>
            setFilter({ status: value === '' ? undefined : (value as 'active' | 'banned') })
          }
          options={[
            { value: 'active', label: 'Active' },
            { value: 'banned', label: 'Banned' },
          ]}
          anyLabel="Any status"
        />
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  anyLabel,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  anyLabel: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium text-navy-900">
        {label}
      </label>
      <NativeSelect id={id} value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        <NativeSelectOption value="">{anyLabel}</NativeSelectOption>
        {options.map((option) => (
          <NativeSelectOption key={option.value} value={option.value}>
            {option.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </div>
  );
}

/** A column header that sorts by this column; a second click flips the direction. */
function SortHeader({ sort, label }: { sort: AdminMemberSort; label: string }) {
  const search = Route.useSearch();
  const active = search.sort === sort;
  // Newest and highest first is the useful default for dates and numbers; names start at A.
  const firstDir = sort === 'lastName' ? 'asc' : 'desc';
  const dir = active ? (search.dir === 'asc' ? 'desc' : 'asc') : firstDir;
  const Arrow = active ? (search.dir === 'asc' ? CaretUp : CaretDown) : null;

  return (
    <Link
      from={Route.fullPath}
      to="/members"
      search={(prev) => ({ ...prev, sort, dir, page: 1 })}
      className="inline-flex min-h-(--spacing-touch) items-center gap-1 whitespace-nowrap hover:text-navy-900"
    >
      {label}
      {Arrow === null ? null : <Arrow size={14} weight="bold" aria-hidden="true" />}
    </Link>
  );
}

function AreaCell({ row }: { row: AdminMemberRow }) {
  if (row.mainAddress === null) return <span className="text-neutral-600">No address</span>;
  const place = [row.mainAddress.suburb, row.mainAddress.city]
    .filter((part, index, parts): part is string => part !== null && parts.indexOf(part) === index)
    .join(', ');
  const more = row.addressCount - 1;
  return (
    <div className="flex flex-col">
      <span className="truncate">{place === '' ? PROVINCE_LABELS[row.mainAddress.province] : place}</span>
      <span className="text-eyebrow text-neutral-600">
        {place === '' ? null : PROVINCE_LABELS[row.mainAddress.province]}
        {more > 0 ? ` · ${more} more ${more === 1 ? 'address' : 'addresses'}` : null}
      </span>
    </div>
  );
}

function StatusCell({ row }: { row: AdminMemberRow }) {
  if (row.status !== 'ACTIVE') return <Badge variant="danger">{USER_STATUS_LABELS[row.status]}</Badge>;
  if (!row.emailVerified) return <Badge variant="warning">Email not verified</Badge>;
  return <Badge variant="success">Active</Badge>;
}

function Pager({ total, search }: { total: number; search: AdminMemberListQueryType }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const pageSizeId = useId();
  const pages = Math.max(1, Math.ceil(total / search.pageSize));
  const first = total === 0 ? 0 : (search.page - 1) * search.pageSize + 1;
  const last = Math.min(total, search.page * search.pageSize);

  return (
    <nav aria-label="Pages" className="flex flex-wrap items-center justify-between gap-3">
      <p className="tabular-nums text-neutral-600">
        {first > total
          ? `Page ${search.page} is past the end`
          : `Showing ${first}–${last} of ${total.toLocaleString('en-ZA')}`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor={pageSizeId} className="text-neutral-600">
            Per page
          </label>
          <NativeSelect
            id={pageSizeId}
            value={String(search.pageSize)}
            onChange={(event) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  pageSize: Number(event.target.value) as AdminMemberListQueryType['pageSize'],
                  page: 1,
                }),
              })
            }
            className="w-20"
          >
            {ADMIN_MEMBER_PAGE_SIZES.map((size) => (
              <NativeSelectOption key={size} value={size}>
                {size}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <Button asChild={search.page > 1} variant="outline" size="sm" disabled={search.page <= 1}>
          {search.page > 1 ? (
            <Link from={Route.fullPath} to="/members" search={(prev) => ({ ...prev, page: search.page - 1 })}>
              <CaretLeft aria-hidden="true" />
              Previous
            </Link>
          ) : (
            <>
              <CaretLeft aria-hidden="true" />
              Previous
            </>
          )}
        </Button>
        <span className="tabular-nums text-neutral-600">
          {Math.min(search.page, pages)} of {pages}
        </span>
        <Button asChild={search.page < pages} variant="outline" size="sm" disabled={search.page >= pages}>
          {search.page < pages ? (
            <Link from={Route.fullPath} to="/members" search={(prev) => ({ ...prev, page: search.page + 1 })}>
              Next
              <CaretRight aria-hidden="true" />
            </Link>
          ) : (
            <>
              Next
              <CaretRight aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
    </nav>
  );
}
